import React, { useEffect, useRef } from 'react';
import { useGoogleIdentity } from '../../hooks/useGoogleIdentity';
import styled from 'styled-components';

const GoogleSignInContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;

const ErrorMessage = styled.div`
  color: #e63946;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
`;

const LoadingMessage = styled.div`
  color: #666;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.5rem;
`;

const GoogleSignIn = ({ 
  clientId, 
  onSignIn, 
  onError, 
  showOneTap = true,
  buttonOptions = {},
  className,
  style 
}) => {
  const buttonRef = useRef(null);
  const {
    isLoaded,
    isInitialized,
    user,
    error,
    initialize,
    handleGoogleSignIn,
    renderSignInButton,
    renderOneTap,
    cancelOneTap
  } = useGoogleIdentity(clientId);

  // Handle Google Sign-In callback
  const handleSignInCallback = (response) => {
    if (response.credential) {
      const userData = handleGoogleSignIn(response.credential);
      if (userData && onSignIn) {
        onSignIn(userData, response);
      }
    } else if (response.error && onError) {
      onError(response.error);
    }
  };

  // Initialize Google Identity Services
  useEffect(() => {
    if (isLoaded && clientId && !isInitialized) {
      initialize(handleSignInCallback);
    }
  }, [isLoaded, clientId, isInitialized, initialize]);

  // Render Google Sign-In button
  useEffect(() => {
    if (isInitialized && buttonRef.current) {
      renderSignInButton(buttonRef.current.id, buttonOptions);
    }
  }, [isInitialized, renderSignInButton, buttonOptions]);

  // Render Google One Tap
  useEffect(() => {
    if (isInitialized && showOneTap && !user) {
      // Delay One Tap to avoid conflicts with button
      const timer = setTimeout(() => {
        renderOneTap();
      }, 1000);

      return () => {
        clearTimeout(timer);
        cancelOneTap();
      };
    }
  }, [isInitialized, showOneTap, user, renderOneTap, cancelOneTap]);

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  if (!clientId) {
    return (
      <ErrorMessage>
        Google Client ID not configured
      </ErrorMessage>
    );
  }

  if (!isLoaded) {
    return (
      <LoadingMessage>
        Loading Google Sign-In...
      </LoadingMessage>
    );
  }

  return (
    <GoogleSignInContainer className={className} style={style}>
      <div 
        ref={buttonRef}
        id="google-signin-button"
        style={{ width: '100%' }}
      />
      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}
    </GoogleSignInContainer>
  );
};

export default GoogleSignIn; 